import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { ProductInstancesTable } from "@/components/product/ProductInstancesTable";
import { ProductError } from "@/components/product/ProductError";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { useAppSelector } from "@/hooks/useAppSelector";
import {
  fetchProductRequest,
  trackProductRequest,
} from "@/store/slices/productSlice";
import { ArrowUpRight, Bookmark, BookmarkCheck } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const {
    currentProduct: product,
    loading,
    error,
    trackingInProgress,
  } = useAppSelector((state) => state.product);

  useEffect(() => {
    if (!id) {
      toast({
        variant: "destructive",
        title: "Invalid Product",
        description: "This product does not exist.",
      });
      navigate("/");
      return;
    }
    dispatch(fetchProductRequest(id));
  }, [id, dispatch, navigate, toast]);

  const handleTrackProduct = () => {
    if (id && product && !product?.is_tracked) {
      dispatch(trackProductRequest(id));
    }
  };

  if (error) {
    return <ProductError />;
  }

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle className="animate-pulse bg-slate-200 h-8 w-1/3 rounded" />
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="animate-pulse bg-slate-200 h-4 w-full rounded" />
              <div className="animate-pulse bg-slate-200 h-4 w-2/3 rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!product) {
    return <ProductError title="Not Found" message="Product not found." />;
  }

  return (
    <div className="container mx-auto p-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>{product?.title_en}</CardTitle>
          <Button
            onClick={handleTrackProduct}
            className={`flex items-center ml-4 gap-2 px-5 py-2.5 text-sm font-medium rounded-lg shadow-md transition ${
              product?.is_tracked
                ? "bg-gray-200 text-gray-700 hover:bg-gray-300"
                : "bg-green-600 text-white hover:bg-green-700"
            }`}
            disabled={product?.is_tracked || trackingInProgress}
          >
            {product?.is_tracked ? (<BookmarkCheck size={18} />) : (<Bookmark size={18} />)}
            {trackingInProgress
              ? "Tracking..."
              : product?.is_tracked
              ? "Tracked"
              : "Track it"}
          </Button>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            <div className="space-y-3">
              <p className="text-base font-semibold text-gray-700">
                Price Range
              </p>
              <div className="flex items-center gap-6">
                <a
                  href={product?.min_product_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Card className="border border-green-500 hover:shadow-md transition-all">
                    <CardContent className="p-4 flex flex-col items-center">
                      <span className="text-sm text-gray-500">Min:</span>
                      <p className="text-2xl font-semibold text-green-600">
                        {product?.min_price}
                      </p>
                      <ArrowUpRight className="w-4 h-4 text-green-500 mt-2" />
                    </CardContent>
                  </Card>
                </a>
                <a
                  href={product?.max_product_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <Card className="border border-orange-500 hover:shadow-md transition-all">
                    <CardContent className="p-4 flex flex-col items-center">
                      <span className="text-sm text-gray-500">Max:</span>
                      <p className="text-2xl font-semibold text-orange-600">
                        {product?.max_price}
                      </p>
                      <ArrowUpRight className="w-4 h-4 text-orange-500 mt-2" />
                    </CardContent>
                  </Card>
                </a>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-medium mb-4">Available Options</h3>
              <ProductInstancesTable instances={product?.instances} />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
